import direction from 'direction';

import { Logger } from '@gitbook/util-debug';
import {
    Set as ISet,
    List,
    memoizeClassMethods,
    OrderedMap,
    OrderedSet,
    Record,
} from '@gitbook/util-immutable';

import { generateKey, isBlockRangeSelection } from '../utils';
import type { Block } from './block';
import { Data } from './data';
import type { Document } from './document';
import type { Fragment, FragmentMap } from './fragment';
import type { Inline } from './inline';
import { isPathBefore, type Key, LookupIndex, type Pair, type Path } from './lookup-index';
import type { Mark } from './mark';
import type { Text } from './text';
import type { TextRange } from './text-range';
import type { SelectionRange } from './value';

// Types only

export type KeyedNode = Document | Block | Inline | Fragment | Text;
export type ParentNode = Document | Block | Fragment;
export type ChildNode = Block | Inline | Text;
export type AncestorNode = Document | Fragment | Block | Inline;

export interface NodeDefaultProps {
    key: string;
    data: Data | object;
    nodes: List<any>;
    fragments: OrderedMap<string, any>;
}

export type NodeIteratorFn<NodeType extends KeyedNode = KeyedNode> = (
    node: NodeType
) => boolean | void;

const logger = Logger('slate:node');

/*
 * Factory to create the common model for nodes.
 */
export abstract class AbstractNode extends Record({
    key: '',
    isVoid: false,
    type: undefined,
    data: Data.create(),
    fragment: undefined,
    fragments: OrderedMap.create(),
    nodes: List.create(),
}) {
    public abstract readonly key: Key;
    public abstract readonly nodes: List<AbstractNode | Text>;
    public abstract readonly type: string | undefined;
    public abstract readonly fragment: string | undefined;
    public abstract readonly data: Data;
    public abstract readonly isVoid: boolean;
    public abstract readonly fragments: FragmentMap;
    public abstract readonly object: 'block' | 'document' | 'text' | 'inline' | 'fragment';

    public isText(): this is Text {
        return false;
    }

    public isBlock(): this is Block {
        return this.object === 'block';
    }

    public isInline(): this is Inline {
        return this.object === 'inline';
    }

    public isDocument(): this is Document {
        return this.object === 'document';
    }

    public isFragment(): this is Fragment {
        return this.object === 'fragment';
    }

    /*
     * getLookupIndex builds a LookupIndex for the current node
     * and all it's descendants.
     * The LookupIndex optimizes common operations by providing a
     * key => path mapping instead of having to recursively walk
     * the node tree for every operation, which becomes very expensive
     * on large documents.
     * NOTE: future iterations will also track block types
     */
    public getLookupIndex(): LookupIndex {
        return new LookupIndex(this.key, this.getIndexPairs());
    }

    public getIndexPairs(): Pair[][] {
        if (!this.nodes && !this.fragments) {
            return [];
        }

        const selfPairs: Pair[] = [];
        selfPairs.length = this.nodes.size + (this.isBlock() ? this.fragments.size : 0);
        const pairs: Pair[][] = [selfPairs];
        this.nodes.forEach((node, i) => {
            // Self
            selfPairs[i] = [node.key, { index: i, parentKey: this.key }];

            // Skip
            if (node.isText()) {
                return;
            }

            // Recurse
            // TODO: explore reducing intermediate transforms by using [][]Pair
            const nested = node.getIndexPairs();
            pairs.push(...nested);
        });

        // Also add the fragments to the IndexPairs, starting at the index after the
        // last node (i.e. if there are 3 nodes [0,1,2], the first fragment will be at idx 3)
        if (this.fragments && this.fragments.size > 0) {
            let iterations = 0;
            this.fragments.forEach((fragment) => {
                const index = this.nodes.size + iterations++;
                selfPairs[index] = [fragment.key, { index, parentKey: this.key }];

                const nested = fragment.getIndexPairs();
                pairs.push(...nested);
            });
        }

        return pairs;
    }

    /**
     * Resolve a path to a child node.
     */
    public getByPath<T extends KeyedNode = KeyedNode>(p: Path): T | null {
        return p.reduce((node: T, idx: number) => {
            if (!node || (!node.nodes && !node.fragments)) {
                return null;
            }

            // If the index is within nodes range, lookup the node
            if (idx < node.nodes.size) {
                return node.nodes.get(idx);
            }
            // Otherwise it's in fragments, lookup the fragment
            const fragments = node.fragments.toArray();
            const offsetIdx = idx - node.nodes.size;
            return fragments[offsetIdx] ? fragments[offsetIdx][1] : null;
        }, this);
    }

    /*
     * Get the path of a descendant node by `key`.
     */
    public getPath(key: Key): Path | null {
        if (key === this.key) {
            return [];
        }
        const index = this.getLookupIndex();
        return index.get(key) || null;
    }

    public getByKey(key: Key): KeyedNode | null {
        const path = this.getPath(key);
        if (!path) {
            return null;
        }
        return this.getByPath(path);
    }

    /*
     * True if the node has both descendants in that order, false otherwise.
     * The order is depth-first, post-order.
     */
    public areDescendantsSorted(first: string, second: string): boolean {
        const firstPath = this.getPath(first);
        const secondPath = this.getPath(second);
        if (!firstPath || !secondPath) {
            return false;
        }

        return isPathBefore(firstPath, secondPath);
    }

    /*
     * Assert that a node has a child by `key` and return it.
     */
    public assertChild(key: string): KeyedNode {
        const child = this.getChild(key);

        if (!child) {
            throw new Error(`Could not find a child node with key "${key}".`);
        }

        return child;
    }

    /*
     * Assert that a node has a descendant by `key` and return it.
     */
    public assertDescendant(key: string): KeyedNode {
        const descendant = this.getDescendant(key);

        if (!descendant) {
            throw new Error(`Could not find a descendant node with key "${key}".`);
        }

        return descendant;
    }

    /*
     * Assert that a node's tree has a node by `key` and return it.
     */
    public assertNode(key: string): KeyedNode {
        const node = this.getNode(key);

        if (!node) {
            throw new Error(`Could not find a node with key "${key}".`);
        }

        return node;
    }

    /*
     * Assert that a node has a fragment by `key` and return it.
     */
    public assertFragment(key: string): Fragment {
        const fragment = this.getFragmentByKey(key);

        if (!fragment) {
            throw new Error(`Could not find a fragment with key "${key}".`);
        }

        return fragment;
    }

    /*
     * Assert that a node's tree has a block by `key` and return it.
     */
    public assertBlock(key: string): Block {
        const node = this.assertNode(key);

        if (node.object !== 'block') {
            throw new Error(`Node "${key}" is not a block.`);
        }

        return node;
    }

    /*
     * Assert that a node exists at `path` and return it.
     */
    public assertPath(path: Path): KeyedNode {
        const descendant = this.getByPath(path);

        if (!descendant) {
            throw new Error(`Could not find a descendant at path "${path}".`);
        }

        return descendant;
    }

    /*
     * Recursively filter all descendant nodes with `iterator`.
     */
    public filterDescendants<T extends KeyedNode = KeyedNode>(
        iterator: NodeIteratorFn<T>,
        options?: {
            withFragments?: boolean;
        }
    ): List<T> {
        const matches: T[] = [];

        this.forEachDescendant<T>((node) => {
            if (iterator(node)) {
                matches.push(node);
            }
        }, options);

        return List.create(matches);
    }

    /*
     * Recursively find a descendant node by `iterator`.
     */
    public findDescendant<T extends KeyedNode = KeyedNode>(
        iterator: NodeIteratorFn<T>,
        options?: {
            withFragments?: boolean;
        }
    ): T | null {
        let found: KeyedNode | null = null;

        this.forEachDescendant<T>((node) => {
            if (iterator(node)) {
                found = node;
                return false;
            }
        }, options);

        return found;
    }

    /*
     * Recursively iterate over all descendant nodes with `iterator`. If the
     * iterator returns `false` it will break the loop.
     */
    public forEachDescendant<T extends KeyedNode = KeyedNode>(
        iterator: NodeIteratorFn<T>,
        options?: {
            withFragments?: boolean;
        }
    ): boolean | undefined {
        let ret;

        // Nodes
        this.nodes.forEach((child) => {
            if (iterator(child) === false) {
                ret = false;
                return false;
            }

            if (child.object !== 'text') {
                ret = child.forEachDescendant(iterator, options);
                return ret;
            }
        });

        // Fragments
        if (options?.withFragments && (this.isBlock() || this.isInline())) {
            this.fragments.toValuesArray().forEach((fragment: Fragment) => {
                if (iterator(fragment) === false) {
                    ret = false;
                    return false;
                }

                return fragment.forEachDescendant(iterator, options);
            });
        }

        return ret;
    }

    public getAncestors(key: string): List<AncestorNode> | null {
        if (key === this.key) {
            return List.create();
        }
        const path = this.getPath(key);
        if (!path) {
            return null;
        }

        const ancestorPaths = path.map((_, idx) => path.slice(0, idx));
        const ancestors = ancestorPaths.map((p) => this.getByPath(p));
        return List.create(ancestors);
    }

    /**
     * Get the path of ancestors of a descendant node by `key`.
     */
    public getFragmentAncestors(key: string): List<AncestorNode> | null {
        if (key === this.key) {
            return List.create();
        }
        const path = this.getPath(key);
        if (!path) {
            return null;
        }

        const ancestorPaths = path.map((_, idx) => path.slice(0, idx));
        let ancestors = List.create();
        let lastSeenKey = null;

        for (let i = ancestorPaths.length - 1; i >= 0; i--) {
            const ancestorNode = this.getByPath(ancestorPaths[i]);
            const atFragmentParent =
                ancestorNode &&
                (ancestorNode.isBlock() || ancestorNode.isInline()) &&
                lastSeenKey !== null &&
                ancestorNode.fragments.some((fragment) => fragment.key === lastSeenKey);

            // As soon as we hit the fragment parent, we stop looking
            // up the tree.
            if (atFragmentParent) {
                break;
            }
            lastSeenKey = ancestorNode.key;
            ancestors = ancestors.unshift(ancestorNode);
        }

        return ancestors;
    }

    /**
     * Get the block descendants of the node.
     * This function doesn't process fragments.
     */
    public getBlocks(): List<Block> {
        const array = this.getBlocksAsArray();
        return List.create(array);
    }

    /**
     * Get the block descendants of the node.
     * This function doesn't process fragments.
     */
    private getBlocksAsArray(): Block[] {
        return this.nodes.reduce((array: Block[], child: Block) => {
            if (child.object !== 'block') {
                return array;
            }
            if (!child.isLeafBlock()) {
                array.push(...child.getBlocksAsArray());
            }
            array.push(child);
            return array;
        }, []);
    }

    /**
     * Get the leaf block descendants of the node.
     * This function doesn't process fragments.
     */
    public getLeafBlocks(): List<Block> {
        const array = this.getLeafBlocksAsArray();
        return List.create(array);
    }

    /**
     * Get the leaf block descendants of the node.
     * This function doesn't process fragments.
     */
    public getLeafBlocksAsArray(): Block[] {
        return this.nodes.reduce((array: Block[], child: Block) => {
            if (child.object !== 'block') {
                return array;
            }
            if (!child.isLeafBlock()) {
                array.push(...child.getLeafBlocksAsArray());
            }
            array.push(child);
            return array;
        }, []);
    }

    /*
     * Get the leaf block descendants in a `range`.
     */
    public getBlocksAtRange(range: SelectionRange): List<Block> {
        if (isBlockRangeSelection(range)) {
            return range.getBlocks(this);
        }
        const array = this.getBlocksAtRangeAsArray(range);
        return List.create(array).unique();
    }

    /*
     * Get the leaf block descendants in a `range` as an array.
     */
    public getBlocksAtRangeAsArray(range: TextRange): Block[] {
        range = range.normalize(this);

        if (range.isUnset) {
            return [];
        }

        const { startKey, endKey } = range;
        const startBlock = this.getClosestBlock(startKey);

        // PERF: the most common case is when the range is in a single block node,
        // where we can avoid a lot of iterating of the tree.
        if (startKey === endKey) {
            return [startBlock];
        }

        const endBlock = this.getClosestBlock(endKey);
        const blocks = this.getBlocksAsArray();
        const start = blocks.indexOf(startBlock);
        const end = blocks.indexOf(endBlock);
        return blocks.slice(start, end + 1);
    }

    /**
     * Get all of the leaf blocks that match a `type`.
     * This function doesn't process fragments.
     */
    public getBlocksByType(type: string, processFragments = false): List<Block> {
        const array = this.getBlocksByTypeAsArray(type, processFragments);
        return List.create(array);
    }

    /**
     * Get all of the blocks that match a `type` as an array.
     * This function processes fragments only if told to do so.
     */
    public getBlocksByTypeAsArray(type: string, processFragments = false): Block[] {
        const result = this.nodes.reduce<Block[]>((array, node) => {
            if (node.object !== 'block') {
                return array;
            }
            if (node.type === type) {
                array.push(node);
            }
            if (node.isLeafBlock() && !processFragments) {
                return array;
            }
            array.push(...node.getBlocksByTypeAsArray(type, processFragments));
            return array;
        }, []);

        if (processFragments && this.fragments) {
            result.push(
                ...this.fragments.reduce<Block[]>(
                    (fragmentArray, fragment) =>
                        fragmentArray.concat(
                            fragment.nodes.reduce<Block[]>((array, node): Block[] => {
                                if (node.object !== 'block') {
                                    return array;
                                }
                                if (node.type === type) {
                                    array.push(node);
                                }
                                if (node.isLeafBlock() && !processFragments) {
                                    return array;
                                }
                                array.push(...node.getBlocksByTypeAsArray(type, processFragments));
                                return array;
                            }, [])
                        ),
                    []
                )
            );
        }

        return result;
    }

    /*
     * Get a child node by `key`.
     */
    public getChild(key: string): ChildNode | null {
        const index = this.getChildIndex(key);
        return index >= 0 ? this.nodes.get(index) : null;
    }

    /*
     * Get the index of a child
     */
    public getChildIndex(key: string): number {
        const path = this.getPath(key);
        if (!path || path.length !== 1) {
            return -1;
        }
        return path[0];
    }

    /**
     * Get closest parent of node by `key` that matches `iterator`.
     */
    public getClosest(
        key: string,
        iterator: (
            ancestor: AncestorNode,
            index: number,
            nodes: List<AncestorNode>
        ) => boolean | void
    ): KeyedNode | null {
        const ancestors = this.getAncestors(key);

        if (!ancestors) {
            throw new Error(`Could not find a descendant node with key "${key}".`);
        }

        // Exclude this node itself.
        return ancestors.rest().findLast(iterator);
    }

    /**
     * Get closest parent of node by `key` that matches `iterator`,
     * restrictred to the fragment of the node.
     */
    public getClosestInFragment(
        key: string,
        iterator: (ancestor: KeyedNode, index: number, nodes: List<KeyedNode>) => boolean | void
    ): KeyedNode | null {
        const ancestors = this.getFragmentAncestors(key);

        if (!ancestors) {
            throw new Error(`Could not find a descendant node with key "${key}".`);
        }

        // Exclude this node itself.
        return ancestors.rest().findLast(iterator);
    }

    /**
     * Get the closest block parent of a `node`.
     */
    public getClosestBlock(key: string): Block | null {
        return this.getClosest(key, (parent) => parent.object === 'block');
    }

    /**
     * Get the closest inline parent of a `node`.
     */
    public getClosestInline(key: string): Inline | null {
        return this.getClosest(key, (parent) => parent.object === 'inline');
    }

    /**
     * Get the closest void parent of a `node`.
     */
    public getClosestVoid(key: string): Block | Inline | null {
        return this.getClosestInFragment(key, (parent) => parent.isVoid);
    }

    /**
     * Get the closest fragment parent of a `node`.
     */
    public getClosestFragment(key: string): Fragment | null {
        return this.getClosest(key, (parent) => parent.object === 'fragment');
    }

    /**
     * Get the closest block or inline ancestor of a `node` that is not in a fragment.
     */
    public getClosestNonFragment(key: string): Block | Inline | null {
        const ancestors = this.getAncestors(key);
        if (!ancestors) {
            return null;
        }

        const outsideFragments = ancestors.takeUntil((ancestor) => ancestor.object === 'fragment');
        return (outsideFragments.last() || null) as Block | Inline | null;
    }

    /**
     * Returns the Fragment parent of the given Node. Returns the Document
     * if Node is not in a Fragment.
     */
    public getFragmentParent(key: string): Fragment | Document | null {
        const ancestors = this.getFragmentAncestors(key);
        if (!ancestors) {
            return null;
        }
        if (ancestors.isEmpty()) {
            return null;
        }

        return ancestors.first() as Fragment | Document;
    }

    /*
     * Get the common ancestor of nodes `one` and `two` by keys.
     */
    public getCommonAncestor(one: string, two: string): KeyedNode | null {
        if (one === this.key) {
            return this;
        }
        if (two === this.key) {
            return this;
        }

        this.assertDescendant(one);
        this.assertDescendant(two);
        let ancestors = List.create();
        let oneParent = this.getParent(one);
        let twoParent = this.getParent(two);

        while (oneParent) {
            ancestors = ancestors.push(oneParent);
            oneParent = this.getParent(oneParent.key);
        }

        while (twoParent) {
            if (ancestors.includes(twoParent)) {
                return twoParent;
            }
            twoParent = this.getParent(twoParent.key);
        }

        return null;
    }

    /*
     * Get the depth of a child node by `key`, with optional `startAt`
     */
    public getDepth(key: string, startAt = 1): number {
        this.assertDescendant(key);
        if (this.hasChild(key)) {
            return startAt;
        }
        return this.getFurthestAncestor(key).getDepth(key, startAt + 1);
    }

    /*
     * Get a descendant node by `key`.
     */
    public getDescendant<T extends KeyedNode = KeyedNode>(key: string): T | null {
        const path = this.getPath(key);
        if (!path) {
            return null;
        }
        return this.getDescendantAtPath<T>(path);
    }

    /*
     * Get a descendant by `path`.
     */
    public getDescendantAtPath<T extends KeyedNode = KeyedNode>(path: Path): T | null {
        if (path.length < 1) {
            return null;
        }
        return this.getByPath<T>(path);
    }

    /*
     * Get the first child text node.
     */
    public getFirstText(): Text | null {
        let descendantFound: Text | null = null;

        const found = this.nodes.find((node) => {
            if (node.object === 'text') {
                return true;
            }
            descendantFound = node.getFirstText();
            return descendantFound;
        });

        return descendantFound || found;
    }

    /*
     * Get the furthest parent of a node by `key` that matches an `iterator`.
     */
    public getFurthest(
        key: string,
        iterator: (node: KeyedNode, index: number, nodes: List<KeyedNode>) => boolean | void
    ): KeyedNode | null {
        const ancestors = this.getAncestors(key);

        if (!ancestors) {
            throw new Error(`Could not find a descendant node with key "${key}".`);
        }

        // Exclude this node itself
        return ancestors.rest().find(iterator);
    }

    /*
     * Get the furthest block parent of a node by `key`.
     */
    public getFurthestBlock(key: string): Block | null {
        return this.getFurthest(key, (node) => node.object === 'block');
    }

    /*
     * Get the furthest inline parent of a node by `key`.
     */
    public getFurthestInline(key: string): Inline | null {
        return this.getFurthest(key, (node) => node.object === 'inline');
    }

    /*
     * Get the furthest ancestor of a node by `key`.
     */
    public getFurthestAncestor(key: string): KeyedNode | null {
        const path = this.getPath(key);
        if (!path) {
            return null;
        }
        return this.getByPath(path.slice(0, 1));
    }

    /*
     * Get the furthest ancestor of a node by `key` that has only one child.
     */
    public getFurthestOnlyChildAncestor(key: string): KeyedNode | null {
        const ancestors = this.getAncestors(key);

        if (!ancestors) {
            throw new Error(`Could not find a descendant node with key "${key}".`);
        }

        const result = ancestors
            // Skip this node...
            .shift()
            // Take parents until there are more than one child...
            .reverse()
            .takeUntil((p) => p.nodes.size > 1)
            // And pick the highest.
            .last();
        if (!result) {
            return null;
        }
        return result;
    }

    /*
     * Return a set of all keys in the node as an array.
     */
    public getKeysAsArray(): string[] {
        const lookupIndex = this.getLookupIndex();
        return lookupIndex.keys();
    }

    /*
     * Return a set of all keys in the node.
     */
    public getKeys(): ISet<string> {
        const keys = this.getKeysAsArray();
        return ISet.create(keys);
    }

    /*
     * Get the last child text node.
     */
    public getLastText(): Text | null {
        let descendantFound = null;

        const found = this.nodes.findLast((node) => {
            if (node.object === 'text') {
                return true;
            }
            descendantFound = node.getLastText();
            return descendantFound;
        });

        return descendantFound || found;
    }

    /**
     * Get all of the marks for all of the characters of every text node.
     */
    public getMarks(): ISet<Mark> {
        const array = this.getMarksAsArray();
        return ISet.create(array);
    }

    /*
     * Get all of the marks for all of the characters of every text node.
     */
    public getOrderedMarks(): OrderedSet<Mark> {
        const array = this.getMarksAsArray();
        return OrderedSet.create(array);
    }

    /*
     * Get all of the marks as an array.
     */
    public getMarksAsArray(): Mark[] {
        // PERF: use only one concat rather than multiple concat
        // becuase one concat is faster
        const result: Mark[] = [];

        this.nodes.forEach((node) => {
            result.push(...node.getMarksAsArray());
        });
        return result;
    }

    /*
     * Get a set of the marks in a `range`.
     */
    public getMarksAtRange(range: TextRange): ISet<Mark> {
        return ISet.create(this.getOrderedMarksAtRange(range));
    }

    /*
     * Get a set of the marks in a `range`.
     */
    public getInsertMarksAtRange(range: TextRange): ISet<Mark> {
        range = range.normalize(this);
        if (range.isUnset) {
            return ISet.create();
        }

        if (range.isCollapsed) {
            // PERF: range is not cachable, use key and offset as proxies for cache
            return this.getMarksAtPosition(range.startKey, range.startOffset);
        }

        const { startKey, startOffset } = range;
        const text = this.getDescendant(startKey);
        if (!text || text.object !== 'text') {
            return ISet.create();
        }

        return text.getMarksAtIndex(startOffset + 1);
    }

    /*
     * Get a set of the marks in a `range`.
     */
    public getOrderedMarksAtRange(range: TextRange): OrderedSet<Mark> {
        range = range.normalize(this);
        if (range.isUnset) {
            return OrderedSet.create();
        }

        if (range.isCollapsed) {
            // PERF: range is not cachable, use key and offset as proxies for cache
            return OrderedSet.create(this.getMarksAtPosition(range.startKey, range.startOffset));
        }

        const { startKey, startOffset, endKey, endOffset } = range;
        return this.getOrderedMarksBetweenPositions(startKey, startOffset, endKey, endOffset);
    }

    /*
     * Get a set of the marks in a `range`.
     * PERF: arguments use key and offset for utilizing cache.
     */
    public getOrderedMarksBetweenPositions(
        startKey: string,
        startOffset: number,
        endKey: string,
        endOffset: number
    ): OrderedSet<Mark> {
        if (startKey === endKey) {
            const startText = this.getDescendant(startKey);
            return startText.getMarksBetweenOffsets(startOffset, endOffset);
        }

        const texts = this.getTextsBetweenPositionsAsArray(startKey, endKey);

        return OrderedSet.create<Mark>().withMutations((result) => {
            texts.forEach((text) => {
                let toAdd: ISet<Mark>;

                if (text.key === startKey) {
                    toAdd = text.getMarksBetweenOffsets(startOffset, text.text.length);
                } else if (text.key === endKey) {
                    toAdd = text.getMarksBetweenOffsets(0, endOffset);
                } else {
                    toAdd = text.getMarks();
                }

                toAdd.forEach((mark) => {
                    result.add(mark);
                });
            });
        });
    }

    /*
     * Get a set of the active marks in a `range`.
     */
    public getActiveMarksAtRange(range: TextRange): ISet<Mark> {
        range = range.normalize(this);
        if (range.isUnset) {
            return ISet.create();
        }

        if (range.isCollapsed) {
            return this.getMarksAtPosition(range.startKey, range.startOffset);
        }

        let { startKey, endKey, startOffset, endOffset } = range;
        let startText = this.getDescendant<Text>(startKey);

        if (startKey !== endKey) {
            while (startKey !== endKey && endOffset === 0) {
                const endPrevText = this.getPreviousText(endKey);
                if (!endPrevText) {
                    break;
                }
                endKey = endPrevText.key;
                endOffset = endPrevText.text.length;
            }

            while (startKey !== endKey && startOffset === startText.text.length) {
                startText = this.getNextText(startKey);
                if (!startText) {
                    break;
                }
                startKey = startText.key;
                startOffset = 0;
            }
        }

        if (!startText) {
            return ISet.create();
        }

        if (startKey === endKey) {
            return startText.getActiveMarksBetweenOffsets(startOffset, endOffset);
        }

        const startMarks = startText.getActiveMarksBetweenOffsets(
            startOffset,
            startText.text.length
        );
        if (startMarks.size === 0) {
            return ISet.create();
        }
        const endText = this.getDescendant<Text>(endKey);

        if (!endText) {
            return ISet.create();
        }

        const endMarks = endText.getActiveMarksBetweenOffsets(0, endOffset);
        let marks = startMarks.intersect(endMarks);
        // If marks is already empty, the active marks is empty
        if (marks.size === 0) {
            return marks;
        }

        let text = this.getNextText(startKey);

        while (text.key !== endKey) {
            if (text.text.length !== 0) {
                marks = marks.intersect(text.getActiveMarks());
                if (marks.size === 0) {
                    return ISet.create();
                }
            }

            text = this.getNextText(text.key);
        }
        return marks;
    }

    /*
     * Get a set of marks in a `position`, the equivalent of a collapsed range.
     */
    public getMarksAtPosition(key: string, offset: number): ISet<Mark> {
        const text = this.getDescendant(key);
        const currentMarks = text.getMarksAtIndex(offset);
        if (offset !== 0) {
            return currentMarks;
        }
        const closestBlock = this.getClosestBlock(key);

        if (closestBlock.text === '') {
            // insert mark for empty block; the empty block are often created by split node or add marks in a range including empty blocks
            return currentMarks;
        }

        const previous = this.getPreviousText(key);
        if (!previous) {
            return ISet.create();
        }

        if (closestBlock.hasDescendant(previous.key)) {
            return previous.getMarksAtIndex(previous.text.length);
        }

        return currentMarks;
    }

    /*
     * Get all of the marks that match a `type`.
     */
    public getMarksByType(type: string): ISet<Mark> {
        const array = this.getMarksByTypeAsArray(type);
        return ISet.create(array);
    }

    /*
     * Get all of the marks that match a `type`.
     */

    public getOrderedMarksByType(type: string): OrderedSet<Mark> {
        const array = this.getMarksByTypeAsArray(type);
        return OrderedSet.create(array);
    }

    /*
     * Get all of the marks that match a `type` as an array.
     */
    public getMarksByTypeAsArray(type: string): Mark[] {
        return this.nodes.reduce((array, node) => {
            if (node.object === 'text') {
                array.push(...node.getMarksAsArray().filter((m) => m.type === type));
            } else {
                array.push(...node.getMarksByTypeAsArray(type));
            }
            return array;
        }, [] as Mark[]);
    }

    /*
     * Get the block node before a descendant text node by `key`.
     */
    public getNextBlock(key: string): Block | null {
        const child = this.assertDescendant(key);
        let last;

        if (child.object === 'block') {
            last = child.getLastText();
        } else {
            const block = this.getClosestBlock(key);
            last = block.getLastText();
        }

        const next = this.getNextText(last.key);
        if (!next) {
            return null;
        }

        return this.getClosestBlock(next.key);
    }

    /*
     * Get the node after a descendant by `key`.
     */
    public getNextSibling(key: string): ChildNode | null {
        const parent = this.getParent(key);
        if (!parent) {
            throw new Error(`Could not find a parent node for key "${key}".`);
        }

        const after = parent.nodes.skipUntil((child) => child.key === key);

        if (after.size === 0) {
            throw new Error(`Could not find a child node with key "${key}".`);
        }
        return after.get(1) || null;
    }

    /**
     * Get the text node after a descendant text node by `key`.
     * If `globalLookup` is true, it will look for the next text node in the whole document, including fragments.
     */
    public getNextText(key: string, globalLookup = false): Text | null {
        const scope = globalLookup ? this : this.getFragmentParent(key);
        if (!scope) {
            return null;
        }

        return (
            scope
                .getTexts(globalLookup)
                .skipUntil((text) => text.key === key)
                .get(1) || null
        );
    }

    /**
     * Get the first text node after a descendant node.
     * Compared to `getNextText`, this method can also work with non-text nodes, and also it processes fragments differently
     * by following the flow of the document.
     */
    public getNextTextAfterNode(key: string, globalLookup = false): Text | null {
        const node = this.assertNode(key);
        if (node.object === 'text') {
            return this.getNextText(node.key, globalLookup);
        }

        const scope = globalLookup ? this : this.getFragmentParent(key);
        if (!scope) {
            return null;
        }

        return (
            scope
                .getTexts(globalLookup)
                .skipUntil((text) => node.hasDescendant(text.key))
                .skipUntil((text) => !node.hasDescendant(text.key))
                .get(0) || null
        );
    }

    /*
     * Get a node in the tree by `key`.
     */
    public getNode(key: string): KeyedNode | null {
        return this.key === key ? this : this.getDescendant(key);
    }

    /*
     * Get a node in the tree by `path`.
     */
    public getNodeAtPath(path: Path): KeyedNode | null {
        return path.length > 0 ? this.getDescendantAtPath(path) : this;
    }

    /*
     * Get the offset for a descendant text node by `key`.
     */
    public getOffset(key: string): number {
        this.assertDescendant(key);

        // Calculate the offset of the nodes before the highest child.
        const child = this.getFurthestAncestor(key);
        const offset = this.nodes
            .takeUntil((n) => n === child)
            .reduce((memo, n) => memo + n.text.length, 0);

        // Recurse if need be.
        return this.hasChild(key) ? offset : offset + child.getOffset(key);
    }

    /*
     * Get the offset from a `range`.
     */
    public getOffsetAtRange(range: TextRange): number {
        range = range.normalize(this);

        if (range.isUnset) {
            throw new Error('The range cannot be unset to calculcate its offset.');
        }

        if (range.isExpanded) {
            throw new Error('The range must be collapsed to calculcate its offset.');
        }

        const { startKey, startOffset } = range;
        return this.getOffset(startKey) + startOffset;
    }

    /*
     * Get the parent of a child node by `key`.
     */
    public getParent(key: string): AncestorNode | null {
        if (this.key === key) {
            return null;
        }

        const path = this.getPath(key);
        if (!path) {
            return null;
        }

        return this.getByPath(path.slice(0, -1));
    }

    /*
     * Refind the path of node if path is changed.
     */
    public refindPath(path: Path, key: string): Path | null {
        const node = this.getByPath(path);

        if (node && node.key === key) {
            return path;
        }

        return this.getPath(key);
    }

    /**
     * Refind the node with the same node.key after change.
     */
    public refindNode(path: Path, key: string): KeyedNode | null {
        const node = this.getByPath(path);

        if (node && node.key === key) {
            return node;
        }

        return this.getByKey(key);
    }

    /*
     * Get the block node before a descendant text node by `key`.
     */
    public getPreviousBlock(key: string): Block | null {
        const child = this.assertDescendant(key);
        let first;

        if (child.object === 'block') {
            first = child.getFirstText();
        } else {
            const block = this.getClosestBlock(key);
            first = block.getFirstText();
        }

        const previous = this.getPreviousText(first.key);
        if (!previous) {
            return null;
        }

        return this.getClosestBlock(previous.key);
    }

    /*
     * Get the node before a descendant node by `key`.
     */
    public getPreviousSibling(key: string): ChildNode | null {
        const parent = this.getParent(key);
        const before = parent.nodes.takeUntil((child) => child.key === key);

        if (before.size === parent.nodes.size) {
            throw new Error(`Could not find a child node with key "${key}".`);
        }

        return before.last();
    }

    /**
     * Get the text node before a descendant text node by `key`.
     * If `globalLookup` is true, it will look for the previous text node in the whole document, including fragments.
     */
    public getPreviousText(key: string, globalLookup = false): Text | null {
        const scope = globalLookup ? this : this.getFragmentParent(key);
        if (!scope) {
            return null;
        }

        return (
            scope
                .getTexts(globalLookup)
                .takeUntil((text) => text.key === key)
                .last() || null
        );
    }

    /**
     * Get the last text node before a descendant node.
     */
    public getPreviousTextBeforeNode(key: string, globalLookup = false): Text | null {
        const node = this.assertNode(key);
        if (node.object === 'text') {
            return this.getPreviousText(node.key, globalLookup);
        }

        const scope = globalLookup ? this : this.getFragmentParent(key);
        if (!scope) {
            return null;
        }

        return (
            scope
                .getTexts(globalLookup)
                .takeUntil((text) => node.hasDescendant(text.key))
                .last() || null
        );
    }

    /*
     * Get the indexes of the selection for a `range`, given an extra flag for
     * whether the node `isSelected`, to determine whether not finding matches
     * means everything is selected or nothing is.
     */
    public getSelectionIndexes(
        range: SelectionRange,
        isSelected = true
    ): { start: number; end: number } | null {
        // PERF: if we're not selected, we can exit early.
        if (!isSelected) {
            return null;
        }

        if (isBlockRangeSelection(range)) {
            return null;
        }

        // if we've been given an invalid selection we can exit early.
        if (range.isUnset) {
            return null;
        }

        const { startKey, endKey } = range;

        // PERF: if the start and end keys are the same, just check for the child
        // that contains that single key.
        if (startKey === endKey) {
            const child = this.getFurthestAncestor(startKey);
            const index = child ? this.getChildIndex(child.key) : null;
            return { start: index, end: index + 1 };
        }

        // Otherwise, check all of the children...
        let start = null;
        let end = null;

        this.nodes.forEach((child, i) => {
            if (child.object === 'text') {
                if (start == null && child.key === startKey) {
                    start = i;
                }
                if (end == null && child.key === endKey) {
                    end = i + 1;
                }
            } else {
                if (start == null && child.hasDescendant(startKey)) {
                    start = i;
                }
                if (end == null && child.hasDescendant(endKey)) {
                    end = i + 1;
                }
            }

            // PERF: exit early if both start and end have been found.
            return start == null || end == null;
        });

        if (isSelected && start == null) {
            start = 0;
        }
        if (isSelected && end == null) {
            end = this.nodes.size;
        }
        return start == null ? null : { start, end };
    }

    /**
     * Get the concatenated text string of all child nodes.
     */
    public getText(blockDelimiter = '', inlineDelimiter = ''): string {
        const delimiter =
            blockDelimiter !== inlineDelimiter &&
            !this.isInline() &&
            !this.isText() &&
            this.nodes.some((child) => child.object === 'block')
                ? blockDelimiter
                : inlineDelimiter;

        return this.nodes
            .toArray()
            .map((node) => node.getText(blockDelimiter, inlineDelimiter))
            .join(delimiter);
    }

    /*
     * Get the descendent text node at an `offset`.
     */
    public getTextAtOffset(offset: number): Text | null {
        // PERF: Add a few shortcuts for the obvious cases.
        if (offset === 0) {
            return this.getFirstText();
        }
        if (offset === this.text.length) {
            return this.getLastText();
        }
        if (offset < 0 || offset > this.text.length) {
            return null;
        }

        let length = 0;

        return this.getTexts().find((node) => {
            length += node.text.length;
            return length > offset;
        });
    }

    /*
     * Get the direction of the node's text.
     */
    public getTextDirection(): 'ltr' | 'rtl' | undefined {
        const dir = direction(this.text);
        return dir === 'neutral' ? undefined : dir;
    }

    /*
     * Recursively get all of the child text nodes in order of appearance.
     */
    public getTexts(withFragments = true): List<Text> {
        const array = this.getTextsAsArray(withFragments);
        return List.create(array);
    }

    /*
     * Recursively get all the leaf text nodes in order of appearance, as array.
     */
    public getTextsAsArray(withFragments: boolean): Text[] {
        const array: Text[] = [];

        this.nodes
            .toArray()
            .concat(
                withFragments && (this.isBlock() || this.isInline())
                    ? this.fragments.toValuesArray()
                    : []
            )
            .forEach((node) => {
                if (node.object === 'text') {
                    array.push(node);
                } else {
                    array.push(...node.getTextsAsArray(withFragments));
                }
            });

        return array;
    }

    /*
     * Get all of the text nodes in a `range`.
     */
    public getTextsAtRange(range: SelectionRange): List<Text> {
        range = range.normalize(this);

        if (isBlockRangeSelection(range)) {
            const { anchorBlock, focusBlock } = range;
            return List.create(this.getTextsBetweenPositionsAsArray(anchorBlock, focusBlock));
        }

        if (range.isUnset) {
            return List.create();
        }
        const { startKey, endKey } = range;
        return !startKey || !endKey
            ? List.create()
            : List.create(
                  this.getTextsBetweenPositionsAsArray(
                      startKey,
                      endKey,
                      this.isInFragment(startKey) || this.isInFragment(endKey)
                  )
              );
    }

    /*
     * Get all of the text nodes in a `range` as an array.
     * PERF: use key in arguments for cache.
     */
    public getTextsBetweenPositionsAsArray(
        startKey: string,
        endKey: string,
        withFragments = false
    ): Text[] {
        const startText = this.getDescendant(startKey);

        if (!startText.isText()) {
            throw new Error(`Expected "${startKey}" to be a text`);
        }

        // PERF: the most common case is when the range is in a single text node,
        // where we can avoid a lot of iterating of the tree.
        if (startKey === endKey) {
            return [startText];
        }

        const endText = this.getDescendant(endKey);
        if (!endText.isText()) {
            throw new Error(`Expected "${endKey}" to be a text`);
        }

        const texts = this.getTextsAsArray(withFragments);
        const start = texts.indexOf(startText);
        const end = texts.indexOf(endText, start);
        return texts.slice(start, end + 1);
    }

    /*
     * Check if a child node exists by `key`.
     */
    public hasChild(key: string): boolean {
        return !!this.getChild(key);
    }

    /*
     * Check if a node has block node children.
     */
    public hasBlocks(key: string): boolean {
        const node = this.assertNode(key);
        return !!node.nodes?.find((n) => n.object === 'block');
    }

    /*
     * Check if a node has inline node children.
     */
    public hasInlines(key: string): boolean {
        const node = this.assertNode(key);
        return !!node.nodes?.find((n) => n.isInline || n.isText);
    }

    /*
     * Recursively check if a child node exists by `key`.
     */
    public hasDescendant(key: string): boolean {
        // Shortcut to prevent using getDescendant that still has to get
        // the actual child. By looking only at the path, we know if the current
        // node includes the key.
        const path = this.getPath(key);
        if (!path) {
            return false;
        }

        return path.length > 0;
    }

    /**
     * Recursively check if a node exists by `key`.
     */
    public hasNode(key: string): boolean {
        if (this.key === key) {
            return true;
        }
        return this.hasDescendant(key);
    }

    /**
     * Check if any of the key contained in `node` can conflict with the keys in the current node.
     * This method can be used to avoid duplicated keys.
     */
    public hasNodeKeyConflict(node: ChildNode): boolean {
        if (this.hasNode(node.key)) {
            return true;
        }

        if (node.object === 'text') {
            return false;
        }

        const nodeLookupIndex = node.getLookupIndex();
        const lookupIndex = this.getLookupIndex();
        const nodeKeys = nodeLookupIndex.keys();
        for (let i = 0; i < nodeKeys.length; i++) {
            if (lookupIndex.has(nodeKeys[i])) {
                return true;
            }
        }
        return false;
    }

    /*
     * Check if a node has a void parent by `key`.
     */
    public hasVoidParent(key: string): boolean {
        return !!this.getClosestVoid(key);
    }

    /**
     * Returns true if the given Node is in a Fragment.
     */
    public isInFragment(key: string): boolean {
        return this.getFragmentParent(key)?.object === 'fragment';
    }

    /**
     * Insert `data` at `dataPath`
     */
    public setData(dataPath: string[], data: any) {
        return this.merge({ data: this.data.setIn(dataPath, data) });
    }

    /**
     * Insert `data` at the `index` of the array stored at `dataPath`.
     * No effect if the path points to a non-array value.
     */
    public insertArrayData(dataPath: string[], data: any, index: number) {
        const prev = this.data.getIn(dataPath);

        if (!Array.isArray(prev)) {
            return this;
        }

        const normalizedIndex = index >= -1 ? index : index + 1;
        const updated =
            index === -1
                ? prev.concat(data)
                : [...prev.slice(0, normalizedIndex), data, ...prev.slice(normalizedIndex)];

        return this.merge({ data: this.data.setIn(dataPath, updated) });
    }

    /**
     * Remove `data` from the array stored at `dataPath`.
     * No effect if the path points to a non-array value.
     *
     * TODO: it should use an index instead of a filter
     */
    public removeArrayData(dataPath: string[], data: any) {
        const prev = this.data.getIn(dataPath);

        if (!Array.isArray(prev)) {
            return this;
        }

        const updated = prev.filter((el) => el !== data);
        return this.merge({ data: this.data.setIn(dataPath, updated) });
    }

    /**
     * Remove data at the given `dataPath`
     */
    public removeData(dataPath: string[]) {
        return this.merge({ data: this.data.deleteIn(dataPath) });
    }

    /**
     * Insert a `node` at `index`.
     */
    public insertNode(index: number, node: ChildNode) {
        if (node.object !== 'text') {
            node = node.mapDescendants((desc) => {
                return this.hasNode(desc.key) ? desc.regenerateKey() : desc;
            });
        }

        const nodes = this.nodes.insert(index, node);
        return this.set('nodes', nodes);
    }

    /**
     * Check whether the node is in a `range`.
     */
    public isInRange(range: TextRange): boolean {
        range = range.normalize(this);
        const { startKey, endKey, isCollapsed } = range;

        // PERF: solve the most common cast where the start or end key are inside
        // the node, for collapsed selections.
        if (
            this.key === startKey ||
            this.key === endKey ||
            this.hasDescendant(startKey) ||
            this.hasDescendant(endKey)
        ) {
            return true;
        }

        // PERF: if the selection is collapsed and the previous check didn't return
        // true, then it must be false.
        if (isCollapsed) {
            return false;
        }

        // Otherwise, look through all of the leaf text nodes in the range, to see
        // if any of them are inside the node.
        const texts = this.getTextsAtRange(range);
        let memo = false;

        texts.forEach((text) => {
            if (this.hasDescendant(text.key)) {
                memo = true;
            }
            return memo;
        });

        return memo;
    }

    /**
     * Check whether the node is a leaf block (only has inline and text nodes).
     */
    public isLeafBlock(): boolean {
        return this.object === 'block' && this.nodes.every((n) => n.object !== 'block');
    }

    /**
     * Check whether the node is a leaf inline.
     */
    public isLeafInline(): boolean {
        return this.object === 'inline' && this.nodes.every((n) => n.object !== 'inline');
    }

    /*
     * Merge a children node `first` with another children node `second`.
     * `first` and `second` will be concatenated in that order.
     * `first` and `second` must be two Nodes or two Text.
     */
    public mergeNode(withIndex: number, index: number): this {
        let one = this.nodes.get(withIndex);
        const two = this.nodes.get(index);

        if (one.object !== two.object) {
            throw new Error(
                `Tried to merge two nodes of different objects: "${one.object}" and "${two.object}".`
            );
        }

        // If the nodes are text nodes, concatenate their leaves together
        if (one.object === 'text') {
            one = one.mergeText(two);
        } else {
            // Otherwise, concatenate their child nodes together.
            const nodes = one.nodes.concat(two.nodes);
            one = one.set('nodes', nodes);
        }

        return this.removeNode(index).removeNode(withIndex).insertNode(withIndex, one);
    }

    /*
     * Map all child nodes, updating them in their parents. This method is
     * optimized to not return a new node if no changes are made.
     */
    public mapChildren(
        iterator: (node: ChildNode, index: number, nodes: List<ChildNode>) => ChildNode
    ): this {
        let { nodes } = this;

        nodes.forEach((node, i) => {
            const ret = iterator(node, i, this.nodes);
            if (ret !== node) {
                nodes = nodes.set(ret.key, ret);
            }
        });

        return this.merge({ nodes });
    }

    /**
     * Flat map all child nodes, updating them in their parents.
     */
    public flatMapChildren(
        iterator: (node: ChildNode, index: number, nodes: List<ChildNode>) => ChildNode[]
    ): this {
        const { nodes } = this;

        const updated = nodes.flatMap((node, i) => {
            return iterator(node, i, this.nodes);
        });

        return this.merge({ nodes: updated });
    }

    /**
     * Map all descendant nodes, updating them in their parents. This method is
     * optimized to not return a new node if no changes are made.
     *
     * Returning null in the iterator removes the node.
     */
    public mapDescendants(
        iterator: (
            node: ChildNode,
            index: number,
            nodes: List<ChildNode>
        ) => ChildNode | ChildNode[] | null
    ): this {
        const nodes: ChildNode[] = [];
        let fragments: Fragment[];
        let changed = false;

        if (this.nodes) {
            this.nodes.forEach((node, index) => {
                let ret = node;
                if (ret.object !== 'text') {
                    ret = ret.mapDescendants(iterator);
                }
                ret = iterator(ret, index, this.nodes);

                if (ret) {
                    if (Array.isArray(ret)) {
                        nodes.push(...ret);
                    } else {
                        nodes.push(ret);
                    }
                }

                if (ret !== node) {
                    changed = true;
                }
            });
        }

        if (this.isBlock() || this.isInline()) {
            fragments = [];
            this.fragments.toValuesArray().forEach((fragment, index) => {
                let ret = fragment;
                ret = ret.mapDescendants(iterator);
                ret = iterator(ret, index, this.fragments);

                if (ret) {
                    if (Array.isArray(ret)) {
                        fragments.push(...ret);
                    } else {
                        fragments.push(ret);
                    }
                }

                if (ret !== fragment) {
                    changed = true;
                }
            });
        }

        if (!changed) {
            return this;
        }

        return this.merge({
            nodes: List.create(nodes),
            ...(fragments
                ? {
                      fragments: OrderedMap.create(
                          fragments.map((fragment) => [fragment.fragmentKey, fragment])
                      ),
                  }
                : {}),
        });
    }

    /**
     * Regenerate the node's key.
     */
    public regenerateKey(): this {
        const key = generateKey();
        return this.merge({ key });
    }

    /**
     * Regenerate keys for all text nodes.
     */
    public regenerateTextKeys(): this {
        return this.mapDescendants((node) => {
            if (node.object === 'text') {
                return node.regenerateKey();
            }
            return node;
        });
    }

    /**
     * Regenerate all keys.
     */
    public regenerateAllKeys(): this {
        const result = this.regenerateKey().mapDescendants((node) => {
            return node.regenerateKey();
        });
        return result;
    }

    /*
     * Remove a `node` from the children node map.
     */
    public removeDescendant(key: string): this {
        let node = this;
        let parent = node.getParent(key);
        if (!parent) {
            throw new Error(`Could not find a descendant node with key "${key}".`);
        }

        const index = parent.getChildIndex(key);
        const nodes = parent.nodes.delete(index);

        parent = parent.merge({ nodes });
        node = node.updateNode(parent);
        return node;
    }

    /*
     * Remove a node at `index`.
     */
    public removeNode(index: number): this {
        const nodes = this.nodes.delete(index);
        return this.merge({ nodes });
    }

    /*
     * Split a child node by `index` at `position`.
     */
    public splitNode(
        /** Index of the child node to split in this node */
        index: number,
        /** Position to split at */
        position: number,
        /** Key to use for the new node part */
        newKey: string = generateKey()
    ): this {
        const child = this.nodes.get(index);
        let one;
        let two;

        // If the child is a text node, the `position` refers to the text offset at
        // which to split it.
        if (child?.object === 'text') {
            [one, two] = child.splitText(position);
        } else {
            // Otherwise, if the child is not a text node, the `position` refers to the
            // index at which to split its children.
            const befores = child.nodes.take(position);
            const afters = child.nodes.skip(position);
            one = child.merge({ nodes: befores });
            two = child.merge({ nodes: afters });
        }

        // Do not allow two keys to be the same in the node tree.
        // This should normally not happen as split_node generates a new key
        // that is passed in the operation's properties. But if it does, we must
        // avoid throwing an error in the LookupIndex at all cost and therefore
        // need to generate a key.
        const keysIdentical = one.get('key') === newKey;
        if (keysIdentical) {
            const fixedNewKey = generateKey();
            logger.warning(
                `Passed key "${newKey}" is identical to the source node one. Generated a new one "${fixedNewKey}"."`
            );
            two = two.set('key', fixedNewKey);
        } else {
            two = two.set('key', newKey);
        }

        // Remove the old node and insert the newly split children.
        return this.removeNode(index).insertNode(index, two).insertNode(index, one);
    }

    /*
     * Set a new value for a child node by `key`.
     */
    public updateNode(node: ChildNode): this {
        if (node.key === this.key) {
            return node;
        }

        let child = this.assertDescendant(node.key);
        const ancestors = this.getAncestors(node.key);

        ancestors.reverse().forEach((parent) => {
            let { nodes, fragments } = parent;
            const index = parent.getChildIndex(child.key);
            child = parent;

            if (node.object === 'fragment' && parent.fragments.has(node.fragmentKey)) {
                fragments = fragments.set(node.fragmentKey, node);
                parent = parent.set('fragments', fragments);
            } else {
                nodes = nodes.set(index, node);
                parent = parent.set('nodes', nodes);
            }

            node = parent;
        });

        return node;
    }

    /**
     * Set a `fragment` on the node.
     */
    public setFragment(fragment: Fragment) {
        const fragments = this.fragments.set(fragment.fragmentKey, fragment);
        return this.merge({ fragments });
    }

    /**
     * Insert a `fragment` on the node.
     */
    public insertFragment(fragment: Fragment, at: number) {
        // Does this fragment has a key (either "fragment" or "key")?
        if (this.hasFragment(fragment.fragmentKey)) {
            // If it's a named fragment ("fragment" is set), we prevent from adding it
            if (fragment.fragment) {
                throw new Error('Named fragment already exist');
            }

            // If not, we generate a unique fragment key for it
            fragment = fragment.regenerateKey();
        }

        if (at > this.fragments.size) {
            throw new Error(
                `The provided index (${at}) cannot be greater than the size of the map (${this.fragments.size})`
            );
        }

        // If the fragment position is last, we do a simple setFragment
        if (at === this.fragments.size) {
            return this.setFragment(fragment);
        }

        // Otherwise, if the fragment position is in-between (ex: when two conflicting
        // insert_fragment were transformed), we reorder the OrderedMap.
        const firstSlice = this.fragments.slice(0, at);
        const secondSlice = this.fragments.slice(at);
        const fragments = firstSlice.set(fragment.fragmentKey, fragment).concat(secondSlice);

        return this.merge({ fragments });
    }

    /**
     * Check whether this Node has the given fragment.
     */
    public hasFragment(key: string): boolean {
        return this.fragments.has(key);
    }

    /**
     * Get a fragment by key.
     */
    public getFragmentByKey(key: string): Fragment | undefined {
        return this.fragments.get(key);
    }

    /**
     * Get a fragment index from a path entry.
     * Note that fragment locators in paths do not represent the index in the fragments array, they are
     * relative to the parent node.
     *
     * e.g. a fragment at path [0, 1] in a parent with 1 node will be at index 0 in the fragments array.
     *
     * This function turns that offset into an index in the fragments array.
     */
    public getFragmentIndexFromPath(path: Path | null): number {
        if (!path || path.length === 0) {
            return -1;
        }

        // Mirrors getIndexPairs
        return path[path.length - 1] - this.nodes.size;
    }

    /**
     * Get a fragment by its type.
     */
    public getFragmentsByType(key: string): Fragment[] {
        return this.fragments.filter((fragment) => fragment.type === key).toValuesArray();
    }

    /**
     * Remove the fragment with `key` from this node.
     */
    public removeFragmentByKey(key: string): this {
        const fragments = this.fragments.delete(key);
        return this.merge({ fragments });
    }

    /**
     * Remove a fragment from this node.
     */
    public removeFragment(fragment: Fragment): this {
        return this.removeFragmentByKey(fragment.fragmentKey);
    }
}

/*
 * Asserts for nodes.
 */
export function assertText(node: KeyedNode): Text {
    if (node.object !== 'text') {
        throw new Error(`Expected node "${node.key}" to be a text`);
    }

    return node;
}

/*
 * Memoize methods for a node model.
 */
export function memoizeMethods(C: any, methods: Array<string | [string, string[]]> = []) {
    memoizeClassMethods(C, [
        'areDescendantsSorted',
        'getAncestors',
        'getBlocksAsArray',
        'getBlocksAtRangeAsArray',
        'getBlocksByTypeAsArray',
        'getLeafBlocksAsArray',
        'getChild',
        'getClosestBlock',
        'getClosestInline',
        'getClosestFragment',
        'getClosestVoid',
        'getCommonAncestor',
        'getDepth',
        'getDescendant',
        'getDescendantAtPath',
        'getFirstText',
        'getFurthestBlock',
        'getFurthestInline',
        'getFurthestAncestor',
        'getFurthestOnlyChildAncestor',
        'getInlinesAtRangeAsArray',
        'getMarksAsArray',
        'getMarksAtPosition',
        'getOrderedMarksBetweenPositions',
        'getInsertMarksAtRange',
        'getKeysAsArray',
        'getLastText',
        'getMarksByTypeAsArray',
        'getNextBlock',
        'getNextSibling',
        'getNextText',
        'getNode',
        'getNodeAtPath',
        'getOffset',
        'getOffsetAtRange',
        'getParent',
        'getPath',
        'getPreviousBlock',
        'getPreviousSibling',
        'getPreviousText',
        'getText',
        'getTextAtOffset',
        'getTextDirection',
        'getTextsAsArray',
        'getTextsBetweenPositionsAsArray',
        'isLeafBlock',
        'isLeafInline',
        'getLookupIndex',
        'getIndexPairs',
        ...methods,
    ]);
}
